// Wires the map-coverage signal (see ../mapCoverageSignal) onto the explore map.
//
// The signal is driven by the Typesense search that populates the map, not by
// what Mapbox happened to have rendered when a timer fired. The old shape
// sampled `queryRenderedFeatures` 1.2s after `moveend`, which raced the search
// it was trying to measure: dragend kicks off the request and moveend starts
// the timer at the same instant, so any round-trip slower than 1.2s was
// recorded as "no trails here". Reading the response directly removes the race
// and the timer along with it.
//
// Deliberately typed against a minimal structural interface rather than
// mapboxgl.Map so this can be unit tested with a plain object — spinning up a
// real GL map in jsdom is not viable.

import {
  createCoverageReporter,
  CoverageContext,
  CoverageReporter,
  ViewportSample,
} from '../mapCoverageSignal';
import { sendMapCoverageEvent } from '../graphql/map-coverage-graph';

export interface CoverageBounds {
  getWest(): number;
  getSouth(): number;
  getEast(): number;
  getNorth(): number;
}

export interface CoverageMapLike {
  getZoom(): number;
  getCenter(): { lng: number; lat: number };
  getBounds(): CoverageBounds | null | undefined;
}

/**
 * Reads the current viewport into a ViewportSample, or null if the map can't
 * answer yet.
 */
export function sampleViewport(
  map: CoverageMapLike | null | undefined,
  hitCount: number,
): ViewportSample | null {
  if (!map) return null;

  try {
    const center = map.getCenter();
    if (!center) return null;

    const sample: ViewportSample = {
      lon: center.lng,
      lat: center.lat,
      zoom: map.getZoom(),
      hitCount,
    };

    const bounds = map.getBounds();
    if (bounds) {
      sample.bounds = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];
    }

    return sample;
  } catch (error) {
    console.debug('[MapCoverage] could not sample viewport', error);
    return null;
  }
}

let sharedReporter: CoverageReporter | null = null;

/**
 * One reporter per page load, so the per-session cell dedupe and event budget
 * span the whole browse rather than resetting on every search.
 */
export function getCoverageReporter(): CoverageReporter {
  if (!sharedReporter) {
    sharedReporter = createCoverageReporter({ send: sendMapCoverageEvent });
  }
  return sharedReporter;
}

export function resetCoverageReporter(): void {
  sharedReporter = null;
}

/**
 * Report the viewport the search just covered, if that search came back empty.
 *
 * Call this once per settled Typesense response. `hitCount` must be the count
 * from the response itself — pass nothing (and we do nothing) when the request
 * failed, because a failed request is not evidence of missing coverage.
 */
export function reportMapCoverage(
  map: CoverageMapLike | null | undefined,
  hitCount: number | null | undefined,
  context: CoverageContext,
  reporter: CoverageReporter = getCoverageReporter(),
): boolean {
  if (typeof hitCount !== 'number' || !Number.isFinite(hitCount)) return false;

  const sample = sampleViewport(map, hitCount);
  if (!sample) return false;

  return reporter.report(sample, context);
}
